type Props = {
  height?: number; // px
  barCount?: number; // odd looks nicest (3,5,7)
  barWidth?: number; // px
  gap?: number; // px between bars
  speed?: number; // 1 = normal, >1 faster
  playing: boolean; // required
  rounded?: boolean; // rounded corners
  restScale?: number; // 0..1 resting amplitude
  color?: string; // defaults to currentColor
  ariaLabel?: string;
} & Omit<React.SVGProps<SVGSVGElement>, 'color' | 'height'>;

export default function EqualizerIconAnimated({
  height = 18,
  barCount = 5,
  barWidth = 3,
  gap = 3,
  speed = 1,
  playing,
  rounded = true,
  restScale = 0.35,
  color,
  ariaLabel = 'Audio playing',
  ...svgProps
}: Props) {
  const totalWidth = barCount * barWidth + (barCount - 1) * gap;
  const fill = color ?? 'currentColor';
  const duration = Math.max(0.8 / Math.max(speed, 0.1), 0.2);

  // a b c b a amplitude pattern (center is largest)
  const amplitudes = React.useMemo(() => {
    const mid = (barCount - 1) / 2;
    const maxAmp = 1,
      minAmp = 0.5;
    const step = mid > 0 ? (maxAmp - minAmp) / mid : 0;
    return Array.from({ length: barCount }, (_, i) => {
      const dist = Math.abs(i - mid);
      return +(maxAmp - dist * step).toFixed(3);
    });
  }, [barCount]);

  const delays = React.useMemo(() => {
    const mid = (barCount - 1) / 2;
    return Array.from({ length: barCount }, (_, i) => {
      const d = Math.abs(i - mid);
      // base ~90ms per “ring”, slight alt to avoid perfect sync
      const base = 0.09 * d + (d > 0 ? 0.03 : 0);
      const alt = ((i - mid) % 2 === 0 ? 1 : -1) * 0.02;
      return +(base + alt).toFixed(3);
    });
  }, [barCount]);

  return (
    <svg
      width={totalWidth}
      height={height}
      viewBox={`0 0 ${totalWidth} ${height}`}
      role="img"
      aria-label={playing ? ariaLabel : 'Audio paused'}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      {...svgProps}
    >
      <style>{`
        @keyframes ez-bounce {
          0%, 100% { transform: scaleY(var(--amp)); }
          50%      { transform: scaleY(calc(var(--amp) * -1)); } /* invert on loop */
        }
        .bar {
          transform-box: fill-box;
          transform-origin: center center; /* mirrored about center */
        }
        .layer {
          transition: opacity 180ms cubic-bezier(.2,.6,.3,1);
          will-change: opacity;
        }
      `}</style>

      {/* Animated layer */}
      <g className="layer" style={{ opacity: playing ? 1 : 0 }}>
        {Array.from({ length: barCount }, (_, i) => {
          const x = i * (barWidth + gap);
          const rx = rounded ? Math.min(barWidth, height) / 2 : 0;
          return (
            <rect
              key={`a-${i}`}
              className="bar"
              x={x}
              y={0}
              width={barWidth}
              height={height}
              rx={rx}
              fill={fill}
              style={{
                // per-bar amplitude + mirrored delay
                // NOTE: duration in seconds; delay in seconds
                animation: `ez-bounce ${duration}s ease-in-out ${delays[i]}s infinite`,
                // CSS var used inside keyframes
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...{ ['--amp' as any]: amplitudes[i] },
              }}
            />
          );
        })}
      </g>

      {/* Rest layer (static, small uniform amplitude), crossfaded in when paused */}
      <g className="layer" style={{ opacity: playing ? 0 : 1 }}>
        {Array.from({ length: barCount }, (_, i) => {
          const x = i * (barWidth + gap);
          const rx = rounded ? Math.min(barWidth, height) / 2 : 0;
          return (
            <rect
              key={`r-${i}`}
              className="bar"
              x={x}
              y={0}
              width={barWidth}
              height={height}
              rx={rx}
              fill={fill}
              style={{ transform: `scaleY(${restScale})` }}
            />
          );
        })}
      </g>
    </svg>
  );
}
